"use client";

import { useState } from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { optimizeCloudinaryUrl } from "@/lib/cloudinary";
import { formatMenuItemPriceLabel } from "@/lib/menu-price-options";
import { itemRequiresCustomization } from "@/lib/menu-item-customization";
import { useCartStore, formatPrice } from "@/stores/cart-store";
import type { Doc } from "../../../convex/_generated/dataModel";

// Fallback sheet for consumers that don't lift selection to a shared,
// surface-level sheet. Lazy so the heavy framer-motion/Convex chunk still only
// loads on first tap. Surfaces that pass onSelect (the ordering menu) render one
// shared sheet instead and never mount this.
const ItemCustomizationSheet = dynamic(
  () =>
    import("./item-customization-sheet").then(
      (mod) => mod.ItemCustomizationSheet,
    ),
  { ssr: false },
);

interface PopularMenuItemCardProps {
  item: Doc<"menu_items">;
  priority?: boolean;
  /**
   * Ids of items with a required modifier group, from
   * menu.getMenuItemIdsWithRequiredModifiers. When null/undefined (browse-only
   * mode, or the set still loading) one-tap quick add stays disabled and the
   * + button opens the customization sheet exactly as before.
   */
  quickAddRequiredIds?: Set<string> | null;
  /**
   * Opens the shared, surface-level customization sheet for this item. Lifting
   * the sheet out of every card means one ItemCustomizationSheet instance (and
   * one framer-motion/Convex-query load) per surface instead of one per card.
   * When omitted, the card falls back to its own local (lazy) sheet.
   */
  onSelect?: (item: Doc<"menu_items">) => void;
}

export function PopularMenuItemCard({
  item,
  priority = false,
  quickAddRequiredIds = null,
  onSelect,
}: PopularMenuItemCardProps) {
  const [showCustomization, setShowCustomization] = useState(false);
  const addItem = useCartStore((state) => state.addItem);

  const imageUrl = item.photoUrl
    ? optimizeCloudinaryUrl(item.photoUrl, 400)
    : null;
  const priceLabel = formatMenuItemPriceLabel(item);

  // One-tap add is only for items with zero required choices; anything with a
  // required template selection or required modifier group keeps the sheet.
  const quickAddEnabled =
    quickAddRequiredIds != null &&
    !itemRequiresCustomization(item, quickAddRequiredIds);

  // Prefer the shared surface-level sheet; fall back to a local one when no
  // onSelect is wired.
  const openCustomization = () => {
    if (onSelect) {
      onSelect(item);
      return;
    }
    setShowCustomization(true);
  };

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!quickAddEnabled) {
      openCustomization();
      return;
    }

    const priceInCents = Math.round(item.price * 100);
    addItem({
      menuItemId: item._id,
      name: item.name,
      price: priceInCents,
      quantity: 1,
      modifiers: [],
      photoUrl: item.photoUrl || undefined,
    });
    toast.success(`${item.name} added to your order`, {
      description: formatPrice(priceInCents),
      duration: 2000,
    });
  };

  const addButtonLabel = quickAddEnabled
    ? `Add ${item.name} to your order`
    : `Customize ${item.name}`;

  return (
    <>
      <div className="group h-full transition-[transform] duration-150 active:scale-[0.985]">
        <div className="relative flex h-full flex-col overflow-hidden rounded-[12px] border border-[#ededed] bg-white transition-[border-color,box-shadow,transform] duration-150 hover:-translate-y-0.5 hover:border-[#d6d6d6] hover:shadow-[0_14px_32px_-24px_rgb(0_0_0_/_0.45)]">
          <button
            type="button"
            onClick={openCustomization}
            aria-label={`View ${item.name}`}
            className="absolute inset-0 z-10 cursor-pointer rounded-[12px] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-[color:var(--roma-terracotta)]"
          />
          {/* Image */}
          <div className="relative aspect-[4/3] w-full overflow-hidden bg-[#f4f4f5]">
            {imageUrl ? (
              <Image
                src={imageUrl}
                alt={item.name}
                fill
                priority={priority}
                loading={priority ? "eager" : "lazy"}
                fetchPriority={priority ? "high" : undefined}
                className="object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]"
                sizes="216px"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-[#f4f4f5]">
                <span className="text-4xl font-bold text-[#0d0d0d]/15">
                  {item.name.charAt(0)}
                </span>
              </div>
            )}

            <div className="absolute right-2 top-2 z-20">
              <button
                onClick={handleAddClick}
                className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-[#0d0d0d] shadow-[0_1px_3px_rgb(0_0_0_/_0.18),0_0_0_1px_rgb(0_0_0_/_0.08)] transition-transform duration-150 hover:scale-105 active:scale-95"
                aria-label={addButtonLabel}
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>

            <div className="absolute bottom-2 left-2 max-w-[calc(100%-1rem)]">
              <div className="rounded-full bg-white px-2.5 py-1 shadow-[0_1px_3px_rgb(0_0_0_/_0.18),0_0_0_1px_rgb(0_0_0_/_0.08)]">
                <span className="block truncate text-[12px] font-bold tabular-nums leading-4 text-[#0d0d0d]">
                  {priceLabel}
                </span>
              </div>
            </div>
          </div>

          {/* Name */}
          <div className="flex flex-1 flex-col p-3">
            <h3 className="line-clamp-2 text-[15px] font-bold leading-5 tracking-normal text-[#0d0d0d] transition-colors group-hover:text-[var(--roma-terracotta)]">
              {item.name}
            </h3>
          </div>
        </div>
      </div>

      {/* Local fallback sheet — only mounts when this card owns its own sheet
          (no onSelect wired) and the guest has actually opened it. */}
      {onSelect ? null : (
        <ItemCustomizationSheet
          item={item}
          open={showCustomization}
          onOpenChange={setShowCustomization}
        />
      )}
    </>
  );
}
